import type {DesignerAction, LayoutField, LayoutSchema, LayoutSection} from '@/types/form-layout'

// ============================================================================
// Helper: generate unique section key
// ============================================================================

function generateSectionKey(): string {
    /* eslint-disable i18next/no-literal-string */
    return `section-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
    /* eslint-enable i18next/no-literal-string */
}

// ============================================================================
// Helper: check if field already exists in layout
// ============================================================================

function fieldAlreadyInLayout(schema: LayoutSchema, field: LayoutField): boolean {
    for (const section of schema.sections) {
        for (const existing of section.fields) {
            if (field.source === 'entity' && existing.source === 'entity') {
                if (existing.fieldKey === field.fieldKey) return true
            } else if (field.source === 'attribute' && existing.source === 'attribute') {
                if (existing.definitionId === field.definitionId) return true
            } else if (field.source === 'relationship' && existing.source === 'relationship') {
                if (existing.relationshipType === field.relationshipType) return true
            }
        }
    }
    return false
}

// ============================================================================
// Reducer
// ============================================================================

export function designerReducer(state: LayoutSchema, action: DesignerAction): LayoutSchema {
    switch (action.type) {
        // eslint-disable-next-line no-restricted-syntax
        case 'ADD_SECTION': {
            const newSection: LayoutSection = {
                key: generateSectionKey(),
                label: 'New Section',
                collapsible: false,
                columns: 1,
                fields: [],
            }
            return {
                ...state,
                sections: [...state.sections, newSection],
            }
        }

        // eslint-disable-next-line no-restricted-syntax
        case 'REMOVE_SECTION': {
            return {
                ...state,
                sections: state.sections.filter((s) => s.key !== action.sectionKey),
            }
        }

        // eslint-disable-next-line no-restricted-syntax
        case 'REORDER_SECTIONS': {
            const sections = [...state.sections]
            const [moved] = sections.splice(action.fromIndex, 1)
            sections.splice(action.toIndex, 0, moved)
            return {...state, sections}
        }

        // eslint-disable-next-line no-restricted-syntax
        case 'ADD_FIELD': {
            // Duplicate guard: reject if same definitionId/fieldKey already present anywhere
            if (fieldAlreadyInLayout(state, action.field)) {
                return state
            }
            const sections = state.sections.map((s) => {
                if (s.key !== action.sectionKey) return s
                return {...s, fields: [...s.fields, action.field]}
            })
            return {...state, sections}
        }

        // eslint-disable-next-line no-restricted-syntax
        case 'REMOVE_FIELD': {
            const sections = state.sections.map((s) => {
                if (s.key !== action.sectionKey) return s
                const fields = s.fields.filter((_, i) => i !== action.fieldIndex)
                return {...s, fields}
            })
            return {...state, sections}
        }

        // eslint-disable-next-line no-restricted-syntax
        case 'REORDER_FIELDS': {
            const sections = state.sections.map((s) => {
                if (s.key !== action.sectionKey) return s
                const fields = [...s.fields]
                const [moved] = fields.splice(action.fromIndex, 1)
                fields.splice(action.toIndex, 0, moved)
                return {...s, fields}
            })
            return {...state, sections}
        }

        // eslint-disable-next-line no-restricted-syntax
        case 'MOVE_FIELD': {
            let movedField: LayoutField | undefined
            const sectionsAfterRemove = state.sections.map((s) => {
                if (s.key !== action.fromSectionKey) return s
                const fields = [...s.fields]
                ;[movedField] = fields.splice(action.fieldIndex, 1)
                return {...s, fields}
            })
            if (!movedField) return state
            const field = movedField
            const sections = sectionsAfterRemove.map((s) => {
                if (s.key !== action.toSectionKey) return s
                const fields = [...s.fields]
                fields.splice(action.toIndex, 0, field)
                return {...s, fields}
            })
            return {...state, sections}
        }

        // eslint-disable-next-line no-restricted-syntax
        case 'UPDATE_FIELD': {
            const sections = state.sections.map((s) => {
                if (s.key !== action.sectionKey) return s
                const fields = s.fields.map((f, i) => {
                    if (i !== action.fieldIndex) return f
                    return {...f, ...action.patch} as LayoutField
                })
                return {...s, fields}
            })
            return {...state, sections}
        }

        // eslint-disable-next-line no-restricted-syntax
        case 'UPDATE_SECTION': {
            const sections = state.sections.map((s) => {
                if (s.key !== action.sectionKey) return s

                const updated = {...s, ...action.patch}

                // When columns change, recalculate colSpan for fields that were using the
                // old default (or whose span no longer fits the new column count).
                if (action.patch.columns !== undefined && action.patch.columns !== s.columns) {
                    const oldDefault = Math.floor(12 / s.columns)
                    const newDefault = Math.floor(12 / action.patch.columns)
                    updated.fields = s.fields.map((f) => {
                        const currentSpan = f.colSpan ?? oldDefault
                        // If field was at the old default span, move it to the new default
                        if (currentSpan === oldDefault) {
                            return {...f, colSpan: newDefault}
                        }
                        // If field span exceeds new default (wouldn't fit in a single column),
                        // clamp it to new default
                        if (currentSpan > newDefault && newDefault < oldDefault) {
                            return {...f, colSpan: newDefault}
                        }
                        return f
                    })
                }

                return updated
            })
            return {...state, sections}
        }

        // eslint-disable-next-line no-restricted-syntax
        case 'RESET': {
            // WHY: backend data may carry undefined for required string fields; this is the
            // single normalisation point so consumers can assume non-null after dispatch.
            return {
                ...action.schema,
                sections: action.schema.sections.map((s) => ({
                    ...s,
                    label: s.label ?? '',
                    collapsible: s.collapsible ?? false,
                    columns: s.columns ?? 1,
                    fields: s.fields ?? [],
                })),
            }
        }

        // eslint-disable-next-line no-restricted-syntax
        case 'LAYOUT_REORDER_OPTIMISTIC': {
            return action.nextSchema
        }

        // eslint-disable-next-line no-restricted-syntax
        case 'LAYOUT_REORDER_REVERT': {
            return action.baseline
        }

        default:
            return state
    }
}

// ============================================================================
// Empty schema factory
// ============================================================================

export function createEmptySchema(): LayoutSchema {
    return {
        schemaVersion: 1,
        gridColumns: 12,
        sections: [],
    }
}

// ============================================================================
// isDirty helper
// ============================================================================

/**
 * Returns true if the draft schema differs from the saved schema.
 * Uses JSON deep-compare.
 */
export function isDirty(draft: LayoutSchema, saved: LayoutSchema | null): boolean {
    if (saved === null) {
        return draft.sections.length > 0
    }
    return JSON.stringify(draft) !== JSON.stringify(saved)
}
